// フォントの読み込みが終わるたびに増える番号。文字の幅は読み込み前（代わりのフォント）と後で変わるため、
// この番号が変わったら測り直す
let fontVersion = 0;
if (typeof document !== 'undefined' && document.fonts) {
  document.fonts.ready.then(() => fontVersion++);
  document.fonts.addEventListener('loadingdone', () => fontVersion++);
}

/**
 * 画面の幅とフォントで決まる計算結果（チェックボックス欄の列数など）を保持する。
 * テンプレートから呼ぶ計算は画面の描画（変更検知）のたびに走るため、文字の入力のたびに文字の幅を測り直さないよう、
 * 本文の幅（縦スクロールバーを除いた幅）が変わったときと、フォントの読み込みが終わったときだけ計算し直す。
 * 縦スクロールバーが出る・消えるときは、画面の幅が変わらなくても本文の幅が変わるため、本文の幅で判定する。
 * 画面の幅が変わったときに描画し直すため、使う側で window:resize を受けておくこと。
 */
export class LayoutCache {
  private values = new Map<string, unknown>();
  private textWidths = new Map<string, number>();
  private width = -1;
  private fontVersion = -1;
  private fontFamily = '';
  private textContext: CanvasRenderingContext2D | null = null;

  /** key ごとの計算結果。本文の幅・フォントが前回と同じなら、前回の結果を返す */
  get<T>(key: string, compute: () => T): T {
    this.refresh();
    if (!this.values.has(key)) {
      this.values.set(key, compute());
    }
    return this.values.get(key) as T;
  }

  /** 文字の幅を、画面と同じフォントで測る（Windows・Mac・iPhone でフォントの幅が違うため） */
  textWidth(text: string, fontSize: number): number {
    this.refresh();
    const key = `${fontSize}|${text ?? ''}`;
    let width = this.textWidths.get(key);
    if (width === undefined) {
      width = this.measure(text ?? '', fontSize);
      this.textWidths.set(key, width);
    }
    return width;
  }

  private refresh(): void {
    if (this.fontVersion !== fontVersion) {
      this.fontVersion = fontVersion;
      this.fontFamily = '';
      this.textWidths.clear();
      this.values.clear();
    }
    const width = document.documentElement.clientWidth;
    if (this.width !== width) {
      this.width = width;
      this.values.clear();
    }
  }

  private measure(text: string, fontSize: number): number {
    if (!this.textContext) {
      this.textContext = document.createElement('canvas').getContext('2d');
    }
    const ctx = this.textContext;
    if (!ctx) {
      return [...text].reduce((w, c) => w + (c.charCodeAt(0) > 0xff ? fontSize : fontSize * 0.56), 0);
    }
    if (!this.fontFamily) {
      this.fontFamily = getComputedStyle(document.body).fontFamily;
    }
    ctx.font = `${fontSize}px ${this.fontFamily}`;
    return ctx.measureText(text).width;
  }
}
